import { useCallback, useEffect, useRef } from 'react';

const HOLD_INITIAL_DELAY_MS = 350;
const HOLD_INTERVAL_MS = 60;

/**
 * Returns `start(action)` / `stop()` handlers that fire `action` once
 * immediately, then repeatedly after a short hold delay — useful for
 * increment/decrement buttons held down by the user.
 */
export function useHoldRepeat() {
  const ref = useRef<{
    holdTimer: number | null;
    intervalId: number | null;
  }>({ holdTimer: null, intervalId: null });

  const stop = useCallback(() => {
    if (ref.current.holdTimer != null) {
      window.clearTimeout(ref.current.holdTimer);
      ref.current.holdTimer = null;
    }
    if (ref.current.intervalId != null) {
      window.clearInterval(ref.current.intervalId);
      ref.current.intervalId = null;
    }
  }, []);

  const start = useCallback(
    (action: () => void) => {
      stop(); // defensive — never have two intervals running
      action();
      ref.current.holdTimer = window.setTimeout(() => {
        ref.current.intervalId = window.setInterval(action, HOLD_INTERVAL_MS);
      }, HOLD_INITIAL_DELAY_MS);
    },
    [stop],
  );

  useEffect(() => stop, [stop]);

  return { start, stop };
}
